import { Component, ViewChild, OnInit, Output, EventEmitter, Input, ChangeDetectionStrategy, OnChanges, SimpleChanges, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { excelWorkbook, Grid as InavistaGrid, GridConfig } from '@inavista/components/grid';
import { CommonService } from 'projects/service/src/app/shared/services/common.service';
import { getCurrentDate } from 'projects/service/src/app/shared/utils/date-functions';
import { LocalRowsGridApi, provideLocalGridRows } from 'projects/service/src/app/shared/utils/local-rows-grid-api';
import { HttpErrorResponse } from '@angular/common/http';
import { CycleCountService } from '../../../cycle-counts.service';
import Swal from 'sweetalert2';
import { MasterItemcodePopupComponent } from 'projects/shared/src/lib/components/master-itemcode-popup/master-itemcode-popup.component';
import {
  buildCycleCountUploadDetailGridConfig, CYCLE_COUNT_UPLOAD_DETAIL_COLUMNS, INVALID_LINE_COLOR, isInvalidUploadLine,
} from './cycle-counts-upload-detail.grid.config';

// The old grid's content was window.innerHeight - 420 tall (- 220 expanded), with its toolbar and header above it and
// the totals row below. The inavista grid draws them inside its own box.
const GRID_CHROME_PX = 122;

@Component({
    selector: 'app-cycle-counts-upload-detail',
    templateUrl: './cycle-counts-upload-detail.component.html',
    styleUrls: ['./cycle-counts-upload-detail.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
    // The grid reads this.uploadDetailData, not an endpoint.
    providers: [...provideLocalGridRows()]
})
export class CycleCountUploadDetailPopupComponent implements OnInit, OnChanges {

  @Output() onClose = new EventEmitter();
  @Output() refreshMainGrid = new EventEmitter();

  @Input() showLoader: boolean = true;
  gridConfig: GridConfig = null;
  gridHeight: number = window.innerHeight - 420 + GRID_CHROME_PX;
  showPopup: boolean = false;
  isExpanded = true
  @Output() browseFile: EventEmitter<any> = new EventEmitter();
  @Output() reloadPopupData: EventEmitter<any> = new EventEmitter();
  @Input() uploadDetailData
  @Input() cycleCountId

  @ViewChild('cycleCountUploadDetailGrid') grid: InavistaGrid;

  USER: any;
  @Input() fileInvalid: boolean = false;
  private readonly gridRows = inject(LocalRowsGridApi);

  constructor(
    private commonService: CommonService,
    private cycleCountService: CycleCountService,
    private dialog: MatDialog,
  ) {
    this.USER = this.commonService.USER;
  }

  showMasterItemCodePopup(eqData: any) {
    if (!eqData?.itemCode) return;
    const dialogRef = this.dialog.open(MasterItemcodePopupComponent, {
      panelClass: 'master-itemcode-popup-dialog',
      width: '80%',
      height: '80%',
      data: eqData
    });
    dialogRef.afterClosed().subscribe((result) => { });
  }

  ngOnInit(): void {
    this.gridRows.columns.set(LocalRowsGridApi.schema(CYCLE_COUNT_UPLOAD_DETAIL_COLUMNS));
    this.gridConfig = buildCycleCountUploadDetailGridConfig();
    this.refreshRows();
    this.showLoader = false;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['uploadDetailData'] && !changes['uploadDetailData'].firstChange) {
      this.refreshRows();
    }
  }

  private lines(): any[] {
    return Array.isArray(this.uploadDetailData) ? this.uploadDetailData : [];
  }

  // The rows the grid shows: the file's lines as the page holds them, numbered.
  refreshRows() {
    this.gridRows.rows.set(this.lines().map((line, i) => ({ ...line, __rowKey: i })));
    this.grid?.refresh();
  }

  /** The page's own line a grid row stands for. */
  lineFor(row: any) {
    return this.lines()[row?.['__rowKey'] as number] ?? row;
  }

  expand() {
    this.isExpanded = !this.isExpanded;
    if (!this.isExpanded) {
      this.gridHeight = window.innerHeight - 220 + GRID_CHROME_PX;
    } else {
      this.gridHeight = window.innerHeight - 420 + GRID_CHROME_PX;
    }
  }

  // The old Excel export, "Upload Detail Data(<date>).xlsx": the rows the grid shows, the visible columns, an invalid
  // line in yellow (the old excelQueryCellInfo).
  async onExportRequested() {
    const rows = this.grid.visibleRows();
    const sheet = this.grid.excelSheet(rows);
    const sheetRows = sheet.rows.map((row, i) => isInvalidUploadLine(rows[i]) ? { ...row, fill: INVALID_LINE_COLOR } : row);
    const blob = await excelWorkbook(sheetRows, sheet.columns, { sheetName: 'Upload Detail Data' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `Upload Detail Data(${getCurrentDate()}).xlsx`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(link.href), 1000);
  }

  fileUpload() {
    if (this.fileInvalid) {
      Swal.fire({
        icon: 'error',
        title: 'File Invalid',
        text: 'Please fix the invalid fields before uploading the file!',
      })
    } else {
      this.showLoader = true
      this.cycleCountService.uploadCycleCountDetail(this.cycleCountId, this.uploadDetailData).subscribe((res: any) => {
        this.reloadPopupData.emit()
        this.onClose.emit();
        this.showLoader = false;
      }, (error: HttpErrorResponse) => {
        this.showLoader = false;
        this.commonService.showNotification('error', error.message)
        throw error;
      });
    }
  }

  close() {
    this.commonService.showConfirmation('Are you sure? All the unsaved changes will be lost')
      .then(result => {
        if (result.isConfirmed) {
          this.onClose.emit();
        }
      })
  }

}
